import { FEATURES } from "@/config/features";

export type NavChild = {
  label: string;
  to: string;
  /**
   * One line of orientation, shown only in the desktop mega panel. It exists
   * for a layout reason as much as an editorial one: the panel is full-bleed,
   * so each cell is ~240px wide, and a bare 42px label left ~200px of the
   * hover target empty. The hint fills the cell the pointer is actually
   * aiming at. Purely navigational — it says where a link goes, never what
   * the product does, so it makes no claim PRODUCT.md has to vouch for.
   */
  hint?: string;
  /** Feature-flagged children render only when true. */
  enabled: boolean;
};

export type NavItem = {
  label: string;
  to: string;
  /** Match the path exactly (used for "/"). */
  end?: boolean;
  enabled: boolean;
  children?: NavChild[];
};

/**
 * The site's primary navigation, defined once and consumed by both the desktop
 * and mobile branches of SiteHeader plus the footers. Previously the same links
 * were hand-written in each place, so every IA change had to be made two or
 * three times and the menus drifted apart.
 *
 * Five top-level items is deliberate: more than that forces the visitor to make
 * a classification decision before they know what the product is.
 */
export const NAV: NavItem[] = [
  { label: "Home", to: "/", end: true, enabled: true },
  {
    label: "About Us",
    to: "/about",
    enabled: true,
    children: [
      {
        label: "About AI2Aim",
        to: "/about",
        hint: "The company and the gap it closes",
        enabled: true,
      },
      {
        label: "Meet Our CEO",
        to: "/about/ceo",
        hint: "Jacob Christian, CEO & Founder",
        enabled: true,
      },
      {
        label: "Careers",
        to: "/jobs",
        hint: "Open roles and how to apply",
        enabled: FEATURES.jobs,
      },
      {
        label: "Forum",
        to: "/forum",
        hint: "Questions, answers and discussion",
        enabled: FEATURES.forum,
      },
    ],
  },
  { label: "Solutions", to: "/solutions", enabled: true },
  { label: "Pricing", to: "/pricing", enabled: FEATURES.pricing },
  { label: "Contact Us", to: "/contact", enabled: true },
];

/** Top-level items that should render, with disabled children stripped. */
export const visibleNav = (): NavItem[] =>
  NAV.filter((item) => item.enabled).map((item) => ({
    ...item,
    children: item.children?.filter((child) => child.enabled),
  }));

/**
 * A parent is active when the current path matches it or any of its children —
 * so "About Us" stays lit while the visitor is on Careers or the Forum.
 */
export const isItemActive = (item: NavItem, pathname: string): boolean => {
  const matches = (to: string, end?: boolean) =>
    end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);

  if (matches(item.to, item.end)) return true;
  return (item.children ?? []).some((child) => child.enabled && matches(child.to));
};
